import React, { useCallback, useEffect, useState } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'

interface PaymentModalProps {
    isOpen: boolean
    onClose: () => void
    authorizationUrl?: string
    isLoading?: boolean
    error?: string | null
}

/**
 * PaymentModal guides users through Paystack payment handoff
 * This component displays the modal and offers manual navigation
 */
export const PaymentModal: React.FC<PaymentModalProps> = ({
    isOpen,
    onClose,
    authorizationUrl,
    isLoading = false,
    error = null,
}) => {
    const [isRedirecting, setIsRedirecting] = useState(false)

    useEffect(() => {
        if (!isOpen) {
            setIsRedirecting(false)
        }
    }, [isOpen])

    useEffect(() => {
        if (authorizationUrl) {
            setIsRedirecting(false)
        }
    }, [authorizationUrl])

    const handleManualRedirect = useCallback(() => {
        if (!authorizationUrl) return
        setIsRedirecting(true)
        window.location.href = authorizationUrl
    }, [authorizationUrl])

    if (!isOpen) return null

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-md w-full mx-4">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                        Payment Processing
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors"
                        aria-label="Close modal"
                    >
                        <XMarkIcon className="w-6 h-6 text-gray-500" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    {error ? (
                        <div className="text-center">
                            <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full mb-4">
                                <XMarkIcon className="w-8 h-8 text-red-600 dark:text-red-400" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                                Payment Error
                            </h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                                {error}
                            </p>
                            <button
                                onClick={onClose}
                                className="w-full px-4 py-2 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600 transition-colors"
                            >
                                Try Again
                            </button>
                        </div>
                    ) : isLoading ? (
                        <div className="text-center">
                            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full mb-4">
                                <div className="animate-spin">
                                    <svg className="w-8 h-8 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                    </svg>
                                </div>
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                                Initializing Payment
                            </h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                                Please wait while we prepare your payment...
                            </p>
                        </div>
                    ) : authorizationUrl ? (
                        <div className="text-center">
                            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full mb-4">
                                <div className="animate-spin">
                                    <svg className="w-8 h-8 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                    </svg>
                                </div>
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                                {isRedirecting ? 'Opening Paystack' : 'Continue to Paystack'}
                            </h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                                {isRedirecting
                                    ? 'We are opening Paystack so you can complete your purchase.'
                                    : 'Click the button below to open Paystack and finish your payment.'}
                            </p>
                            <button
                                onClick={handleManualRedirect}
                                disabled={isRedirecting}
                                className="w-full px-4 py-2 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600 transition-colors disabled:bg-blue-300 disabled:cursor-not-allowed"
                            >
                                {isRedirecting ? 'Opening...' : 'Continue to Paystack'}
                            </button>
                            <div className="mt-4 text-xs text-gray-500 dark:text-gray-400">
                                If the page does not open, check your pop-up settings and try again.
                            </div>
                        </div>
                    ) : (
                        <div className="text-center">
                            <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full mb-4">
                                <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24">
                                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m0 0h6m-6-6H6m0 0H0" />
                                </svg>
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                                Ready for Payment
                            </h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                                Click the button below to close this modal and try again.
                            </p>
                            <button
                                onClick={onClose}
                                className="w-full px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    )}
                </div>

                {/* Footer Note */}
                <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700 rounded-b-lg">
                    <p className="text-xs text-gray-600 dark:text-gray-400 text-center">
                        Your payment is secure and handled by Paystack
                    </p>
                </div>
            </div>
        </div>
    )
}